import { Button, Empty, Grid, Popconfirm, Switch, Table, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { KcRow } from '../types';
import {
  TREND_META,
  creatorName,
  formatDateTimeShort,
  formatDayMonth,
  formatNumber,
  milestoneColor,
  trendInk,
} from '../utils';
import styles from '../style.module.scss';

const PAGE_SIZE = 50;

interface Props {
  rows: KcRow[];
  loading: boolean;
  milestoneOrder: number[];
  /** tiktokUid đang gửi PATCH ẩn / hiện hoặc thêm / gỡ loại trừ. */
  pending: Record<string, boolean>;
  onToggleHidden: (row: KcRow, hidden: boolean) => void;
  /** true = thêm vào danh sách loại trừ (mọi tháng); false = gỡ. */
  onToggleExcluded: (row: KcRow, excluded: boolean) => void;
  emptyText: string;
}

const rowClassName = (row: KcRow) =>
  row.hidden || row.excluded ? styles.rowHidden : row.honored ? '' : styles.rowBelow;

/**
 * Bảng mỗi Creator một dòng (mốc cao nhất chưa tụt). Thứ tự mặc định do trang sắp sẵn (mốc
 * giảm dần rồi KC giảm dần); bấm tiêu đề cột để sắp lại.
 */
const MilestonesTable = ({
  rows,
  loading,
  milestoneOrder,
  pending,
  onToggleHidden,
  onToggleExcluded,
  emptyText,
}: Props) => {
  // Màn hẹp: không ghim cột công tắc bên phải, để phần nhìn thấy của bảng không bị chiếm nửa.
  const pinRight = Grid.useBreakpoint().md !== false;

  // Cột nào cũng có bề rộng: chữ Be Vietnam Pro rộng, cột hẹp quá sẽ xuống dòng từng chữ.
  const columns: ColumnsType<KcRow> = [
    {
      title: 'Creator',
      key: 'creator',
      width: 230,
      render: (_, row) => (
        <div className={styles.person}>
          <span className={styles.nameLine}>
            <span className={styles.personName}>{creatorName(row.username, row.tiktokUid)}</span>
            {row.excluded && (
              <span className={styles.rowTags}>
                <Tag color="red">Đã loại trừ</Tag>
              </span>
            )}
          </span>
          {row.groupName && <span className={styles.personSub}>{row.groupName}</span>}
          {(row.userId !== null || !row.honored) && (
            <span className={styles.rowTags}>
              {row.userId !== null && <Tag color="green">Đã liên kết</Tag>}
              {!row.honored && <Tag className={styles.belowTag}>Dưới mốc vinh danh</Tag>}
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Mốc cao nhất',
      key: 'milestone',
      width: 140,
      sorter: (a, b) => a.milestone - b.milestone || a.diamonds - b.diamonds,
      render: (_, row) => (
        <Tag color={milestoneColor(row.milestone, milestoneOrder)}>{row.label}</Tag>
      ),
    },
    {
      title: 'Ngày đạt',
      key: 'reachedDate',
      width: 105,
      sorter: (a, b) => a.reachedDate.localeCompare(b.reachedDate),
      render: (_, row) => <span>{formatDayMonth(row.reachedDate)}</span>,
    },
    {
      title: 'Các mốc',
      key: 'steps',
      width: 260,
      render: (_, row) =>
        row.steps.length ? (
          <div className={styles.steps}>
            {row.steps.map((step) => {
              const text = `${step.label} ${formatDayMonth(step.dataDate)}`;
              return step.dropped ? (
                <Tooltip key={step.milestone} title="Đã tụt dưới mốc sau khi sửa số">
                  <Tag className={styles.stepDropped}>
                    <s>{text}</s>
                  </Tag>
                </Tooltip>
              ) : (
                <Tag key={step.milestone} color={milestoneColor(step.milestone, milestoneOrder)}>
                  {text}
                </Tag>
              );
            })}
          </div>
        ) : (
          <span>—</span>
        ),
    },
    {
      title: 'KC tháng',
      key: 'diamonds',
      width: 135,
      align: 'right',
      sorter: (a, b) => a.diamonds - b.diamonds,
      render: (_, row) => <strong>{formatNumber(row.diamonds)}</strong>,
    },
    {
      title: 'Phân tích',
      key: 'analysis',
      width: 340,
      render: (_, row) => {
        const analysis = row.analysis;
        if (!analysis?.text) return <span>—</span>;
        const trend = analysis.trend;
        return (
          <span
            className={styles.analysis}
            style={{ color: trendInk(trend) }}
            title={trend ? TREND_META[trend].label : TREND_META.NONE.label}
          >
            {analysis.text}
          </span>
        );
      },
    },
    {
      title: 'Push',
      key: 'pushedAt',
      width: 170,
      render: (_, row) =>
        row.pushedAt ? (
          <span className={styles.small}>Đã gửi {formatDateTimeShort(row.pushedAt)}</span>
        ) : (
          <span>—</span>
        ),
    },
    {
      title: 'Hiển thị',
      key: 'hidden',
      width: 180,
      fixed: pinRight ? 'right' : undefined,
      className: styles.visibilityCell,
      render: (_, row) => {
        const name = creatorName(row.username, row.tiktokUid);
        const busy = !!pending[row.tiktokUid];
        return (
          <div className={styles.visibility}>
            <Tooltip
              title={row.excluded ? 'Đang trong danh sách loại trừ' : 'Chỉ ẩn trong tháng này'}
            >
              {/* span: Tooltip vẫn hiện khi công tắc bị khoá. */}
              <span>
                <Switch
                  checked={!row.hidden && !row.excluded}
                  loading={busy}
                  disabled={row.excluded}
                  checkedChildren="Hiện"
                  unCheckedChildren="Ẩn"
                  aria-label={`${row.hidden ? 'Hiện lại' : 'Ẩn'} ${name} trong tháng này`}
                  onChange={(checked) => onToggleHidden(row, !checked)}
                />
              </span>
            </Tooltip>
            {row.hidden && !row.excluded && (
              <span className={styles.hiddenBy}>
                {row.hiddenByName ? `Ẩn bởi ${row.hiddenByName}` : 'Đã ẩn'}
              </span>
            )}
            {row.excluded ? (
              <Button
                type="link"
                size="small"
                className={styles.rowAction}
                disabled={busy}
                onClick={() => onToggleExcluded(row, false)}
              >
                Gỡ loại trừ
              </Button>
            ) : (
              <Popconfirm
                title={`Loại trừ ${name} ở mọi tháng?`}
                description="Vẫn ghi mốc và phân tích, nhưng không hiện trên Trang chủ app và không push."
                okText="Loại trừ"
                cancelText="Huỷ"
                onConfirm={() => onToggleExcluded(row, true)}
              >
                <Button type="link" size="small" className={styles.rowAction} disabled={busy}>
                  Loại trừ luôn
                </Button>
              </Popconfirm>
            )}
          </div>
        );
      },
    },
  ];

  const tableWidth = columns.reduce((sum, column) => sum + (Number(column.width) || 0), 0);

  return (
    <div className="custom-table">
      <Table
        size="small"
        rowKey="tiktokUid"
        loading={loading}
        dataSource={rows}
        columns={columns}
        scroll={{ x: tableWidth }}
        rowClassName={rowClassName}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />,
        }}
        pagination={{
          pageSize: PAGE_SIZE,
          hideOnSinglePage: true,
          showSizeChanger: false,
          showLessItems: true,
        }}
      />
    </div>
  );
};

export default MilestonesTable;
